// Comprehensive error handling utilities for the PVT webapp

export interface NetworkError extends Error {
  code?: string;
  status?: number;
  isNetworkError: boolean;
  retryable: boolean;
}

export interface ValidationError extends Error {
  field?: string;
  code?: string;
  isValidationError: boolean;
}

export type AppErrorType = 'network' | 'validation' | 'timing' | 'data' | 'auth' | 'session' | 'permission' | 'not_found' | 'server' | 'unknown';

export interface AppError extends Error {
  code?: string;
  type: AppErrorType;
  retryable: boolean;
  userMessage: string;
  originalError?: Error;
}

// Error type guards
export const isNetworkError = (error: any): error is NetworkError => {
  return error && (error.isNetworkError === true || error.code === 'NETWORK_ERROR');
};

export const isValidationError = (error: any): error is ValidationError => {
  return error && (error.isValidationError === true || error.code === 'VALIDATION_ERROR');
};

export const isAppError = (error: any): error is AppError => {
  return error && error.type && error.userMessage;
};

// Error message patterns for more specific classification
const AUTH_ERROR_PATTERNS = [
  'invalid email or password',
  'invalid credentials',
  'incorrect password',
  'wrong password',
  'email or password',
  'authentication failed',
  'unauthorized',
  'invalid token',
  'token expired',
  'session expired',
];

const PERMISSION_ERROR_PATTERNS = [
  'permission denied',
  'access denied',
  'forbidden',
  'not authorized',
  'unauthorized access',
  'admin',
];

const NOT_FOUND_ERROR_PATTERNS = [
  'not found',
  'does not exist',
  'could not find',
  'invalid id',
  'no such',
  'session not found',
  'kiosk session not found',
  'employee not found',
  'user not found',
  'test not found',
  'company not found',
  'record not found',
];

const VALIDATION_ERROR_PATTERNS = [
  'is required',
  'must be',
  'cannot be',
  'invalid',
  'incorrect',
  'already exists',
  'duplicate',
  'age must be',
];

const NETWORK_ERROR_PATTERNS = [
  'network',
  'connection',
  'offline',
  'internet',
];

const TIMEOUT_ERROR_PATTERNS = [
  'timeout',
  'timed out',
  'took too long',
  'request took',
];

const DATA_ERROR_PATTERNS = [
  'storage',
  'save',
  'persist',
  'load data',
  'retrieve',
];

// Helper to check if message contains any pattern (case insensitive)
const messageContains = (error: any, patterns: string[]): boolean => {
  const msg = (error.message || '').toLowerCase();
  return patterns.some(pattern => msg.includes(pattern.toLowerCase()));
};

// Error classification
export const classifyError = (error: any): AppError => {
  const errorMessage = error.message || '';
  const errorCode = error.code || '';
  const status = error.response?.status;
  // Extract backend error message if available
  const backendMessage = error.response?.data?.error?.message || error.response?.data?.message || '';
  const combinedMessage = backendMessage || errorMessage;
  
  // Check for HTTP status codes first (most specific)
  if (status) {
    // Authentication errors (401)
    if (status === 401) {
      return {
        name: 'AuthenticationError',
        message: backendMessage || errorMessage || 'Authentication required',
        code: error.response?.data?.error?.code || 'AUTH_ERROR',
        type: 'auth',
        retryable: false,
        userMessage: backendMessage || 'Invalid email or password. Please try again.',
        originalError: error,
      };
    }
    
    // Permission/Forbidden errors (403)
    if (status === 403) {
      return {
        name: 'PermissionError',
        message: backendMessage || errorMessage || 'Access denied',
        code: error.response?.data?.error?.code || 'PERMISSION_DENIED',
        type: 'permission',
        retryable: false,
        userMessage: backendMessage || 'Access denied. Please contact your administrator.',
        originalError: error,
      };
    }
    
    // Not Found errors (404)
    if (status === 404) {
      return {
        name: 'NotFoundError',
        message: backendMessage || errorMessage || 'Resource not found',
        code: error.response?.data?.error?.code || 'NOT_FOUND',
        type: 'not_found',
        retryable: false,
        userMessage: backendMessage || 'The requested resource could not be found.',
        originalError: error,
      };
    }
    
    // Validation errors (400)
    if (status === 400) {
      return {
        name: 'ValidationError',
        message: backendMessage || errorMessage || 'Invalid request',
        code: error.response?.data?.error?.code || 'VALIDATION_ERROR',
        type: 'validation',
        retryable: false,
        userMessage: backendMessage || 'Please check your input and try again.',
        originalError: error,
      };
    }
    
    // Server errors (5xx)
    if (status >= 500) {
      return {
        name: 'ServerError',
        message: backendMessage || errorMessage || 'Server error',
        code: error.response?.data?.error?.code || 'SERVER_ERROR',
        type: 'server',
        retryable: true,
        userMessage: 'Server is temporarily unavailable. Please try again in a moment.',
        originalError: error,
      };
    }
    
    // Other client errors (4xx)
    if (status >= 400 && status < 500) {
      return {
        name: 'ClientError',
        message: backendMessage || errorMessage || 'Request failed',
        code: error.response?.data?.error?.code || 'CLIENT_ERROR',
        type: 'unknown',
        retryable: false,
        userMessage: backendMessage || 'Request failed. Please try again.',
        originalError: error,
      };
    }
  }
  
  // Network errors - check error code (status codes already handled above, so 401/auth won't be misclassified here)
  if (errorCode === 'NETWORK_ERROR' || errorCode === 'ERR_NETWORK' || error.name === 'NetworkError') {
    return {
      name: 'NetworkError',
      message: errorMessage || 'Network connection failed',
      code: errorCode || 'NETWORK_ERROR',
      type: 'network',
      retryable: true,
      userMessage: messageContains(error, NETWORK_ERROR_PATTERNS)
        ? errorMessage || 'Unable to connect to the server. Please check your internet connection.'
        : 'Connection problem. Please check your internet connection and try again.',
      originalError: error,
    };
  }
  
  // Browser offline (after status check - won't mask 401/403)
  if (!navigator.onLine) {
    return {
      name: 'NetworkError',
      message: 'No internet connection',
      code: 'OFFLINE',
      type: 'network',
      retryable: true,
      userMessage: 'You appear to be offline. Please check your internet connection and try again.',
      originalError: error,
    };
  }
  
  // Timeout errors
  if (errorCode === 'ECONNABORTED' || messageContains(error, TIMEOUT_ERROR_PATTERNS)) {
    return {
      name: 'TimeoutError',
      message: errorMessage || 'Request timed out',
      code: 'TIMEOUT_ERROR',
      type: 'network',
      retryable: true,
      userMessage: messageContains(error, TIMEOUT_ERROR_PATTERNS)
        ? errorMessage || 'The request took too long. Please try again.'
        : 'The request timed out. Please check your connection and try again.',
      originalError: error,
    };
  }
  
  // Session expired errors
  if (messageContains(error, ['session', 'expired']) || errorCode === 'SESSION_EXPIRED') {
    return {
      name: 'SessionError',
      message: errorMessage || 'Session expired',
      code: 'SESSION_EXPIRED',
      type: 'session',
      retryable: false,
      userMessage: 'Your session has expired. Please log in again to continue.',
      originalError: error,
    };
  }
  
  // Authentication errors - check message patterns
  if (messageContains(error, AUTH_ERROR_PATTERNS) || errorCode === 'AUTH_ERROR') {
    return {
      name: 'AuthenticationError',
      message: errorMessage || 'Authentication failed',
      code: 'AUTH_ERROR',
      type: 'auth',
      retryable: false,
      userMessage: errorMessage || 'Invalid email or password. Please try again.',
      originalError: error,
    };
  }
  
  // Permission errors - check message patterns
  if (messageContains(error, PERMISSION_ERROR_PATTERNS) || errorCode === 'PERMISSION_DENIED') {
    return {
      name: 'PermissionError',
      message: errorMessage || 'Permission denied',
      code: 'PERMISSION_DENIED',
      type: 'permission',
      retryable: false,
      userMessage: errorMessage || 'You do not have permission to perform this action.',
      originalError: error,
    };
  }
  
  // Not found errors - check message patterns
  if (messageContains(error, NOT_FOUND_ERROR_PATTERNS) || errorCode === 'NOT_FOUND') {
    return {
      name: 'NotFoundError',
      message: errorMessage || 'Resource not found',
      code: 'NOT_FOUND',
      type: 'not_found',
      retryable: false,
      userMessage: errorMessage || 'The requested resource was not found.',
      originalError: error,
    };
  }
  
  // Validation errors - check message patterns
  if (messageContains(error, VALIDATION_ERROR_PATTERNS) || errorCode === 'VALIDATION_ERROR') {
    return {
      name: 'ValidationError',
      message: errorMessage || 'Validation failed',
      code: 'VALIDATION_ERROR',
      type: 'validation',
      retryable: false,
      userMessage: errorMessage || 'Please check your input and try again.',
      originalError: error,
    };
  }
  
  // Timing-related errors
  if (messageContains(error, ['timing']) || errorCode === 'TIMING_ERROR') {
    return {
      name: 'TimingError',
      message: errorMessage || 'Timing measurement failed',
      code: 'TIMING_ERROR',
      type: 'timing',
      retryable: false,
      userMessage: 'There was an issue with timing measurement during the test. Please restart the test.',
      originalError: error,
    };
  }
  
  // Data persistence errors
  if (messageContains(error, DATA_ERROR_PATTERNS) || errorCode === 'DATA_PERSISTENCE_ERROR') {
    return {
      name: 'DataError',
      message: errorMessage || 'Data storage failed',
      code: 'DATA_PERSISTENCE_ERROR',
      type: 'data',
      retryable: true,
      userMessage: errorMessage || 'Unable to save data. Please try again.',
      originalError: error,
    };
  }
  
  // Check for specific domain errors based on message content
  // Kiosk-specific errors
  if (messageContains(error, ['kiosk'])) {
    return {
      name: 'KioskError',
      message: errorMessage,
      code: 'KIOSK_ERROR',
      type: 'session',
      retryable: false,
      userMessage: errorMessage || 'Kiosk session error. Please restart the test.',
      originalError: error,
    };
  }
  
  // Employee/test errors
  if (messageContains(error, ['employee', 'test'])) {
    return {
      name: 'TestError',
      message: errorMessage,
      code: 'TEST_ERROR',
      type: 'data',
      retryable: true,
      userMessage: errorMessage || 'There was an issue with the test. Please try again.',
      originalError: error,
    };
  }
  
  // Unknown errors
  return {
    name: 'UnknownError',
    message: errorMessage || 'An unexpected error occurred',
    code: 'UNKNOWN_ERROR',
    type: 'unknown',
    retryable: false,
    userMessage: errorMessage || 'An unexpected error occurred. Please refresh the page and try again.',
    originalError: error,
  };
};

// Retry configuration
export interface RetryConfig {
  maxAttempts: number;
  baseDelay: number;
  maxDelay: number;
  backoffFactor: number;
  retryableErrors: string[];
}

export const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxAttempts: 3,
  baseDelay: 1000, // 1 second
  maxDelay: 10000, // 10 seconds
  backoffFactor: 2,
  retryableErrors: [
    'NETWORK_ERROR', 
    'TIMEOUT_ERROR', 
    'SERVER_ERROR', 
    'DATA_PERSISTENCE_ERROR',
    'ERR_NETWORK',
    'OFFLINE',
  ],
};

// Exponential backoff delay calculation
export const calculateRetryDelay = (attempt: number, config: RetryConfig): number => {
  const delay = config.baseDelay * Math.pow(config.backoffFactor, attempt - 1);
  return Math.min(delay, config.maxDelay);
};

// Check if error is retryable
export const isRetryableError = (error: AppError, config: RetryConfig = DEFAULT_RETRY_CONFIG): boolean => {
  return error.retryable && config.retryableErrors.includes(error.code || '');
};

// Sleep utility for retry delays
export const sleep = (ms: number): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

// Retry wrapper function
export const withRetry = async <T>(
  operation: () => Promise<T>,
  config: RetryConfig = DEFAULT_RETRY_CONFIG,
  onRetry?: (attempt: number, error: AppError) => void
): Promise<T> => {
  let lastError: AppError;

  for (let attempt = 1; attempt <= config.maxAttempts; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = classifyError(error);
      
      // Don't retry if it's the last attempt or error is not retryable
      if (attempt === config.maxAttempts || !isRetryableError(lastError, config)) {
        throw lastError;
      }

      // Call retry callback if provided
      onRetry?.(attempt, lastError);

      // Wait before retrying
      const delay = calculateRetryDelay(attempt, config);
      await sleep(delay);
    }
  }

  throw lastError!;
};

// Error logging utility
export const logError = (error: AppError, context?: string): void => {
  const errorInfo = {
    message: error.message,
    code: error.code,
    type: error.type,
    context,
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    url: window.location.href,
    stack: error.originalError?.stack || error.stack,
  };

  // Log to console in development
  if (process.env.NODE_ENV === 'development') {
    console.error('Application Error:', errorInfo);
  }

  // In production, you might want to send to an error tracking service
  // Example: sendToErrorTrackingService(errorInfo);
};

// User-friendly error messages
export const getErrorMessage = (error: any): string => {
  if (isAppError(error)) {
    return error.userMessage;
  }

  const classified = classifyError(error);
  return classified.userMessage;
};

// Network connectivity checker
let lastConnectivityCheckAt = 0;
let lastConnectivityStatus = navigator.onLine;

export const checkNetworkConnectivity = async (): Promise<boolean> => {
  if (!navigator.onLine) {
    return false;
  }

  try {
    const now = Date.now();
    if (now - lastConnectivityCheckAt < 5000) {
      return lastConnectivityStatus;
    }

    // Try to fetch a small resource to verify actual connectivity
    const response = await fetch('/health', {
      method: 'HEAD',
      cache: 'no-cache',
      signal: AbortSignal.timeout(1500), // 1.5 second timeout
    });
    lastConnectivityStatus = response.ok;
    lastConnectivityCheckAt = now;
    return lastConnectivityStatus;
  } catch {
    lastConnectivityStatus = false;
    lastConnectivityCheckAt = Date.now();
    return false;
  }
};

// Graceful degradation helper
export const withGracefulDegradation = async <T>(
  primaryOperation: () => Promise<T>,
  fallbackOperation?: () => Promise<T> | T,
  fallbackValue?: T
): Promise<T | null> => {
  try {
    return await primaryOperation();
  } catch (error) {
    const appError = classifyError(error);
    logError(appError, 'Graceful degradation triggered');

    if (fallbackOperation) {
      try {
        return await fallbackOperation();
      } catch (fallbackError) {
        logError(classifyError(fallbackError), 'Fallback operation failed');
      }
    }

    return fallbackValue ?? null;
  }
};
